import React, { useState, useEffect } from "react";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import { docsColors } from "@/lib/docs-theme";

interface CodeBlockProps {
  language?: "bash" | "json" | "javascript" | "python" | "ruby" | "php" | "http";
  children: string;
  title?: string;
  className?: string;
}

export default function CodeBlock({
  language = "bash",
  children,
  title,
  className = "",
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
    } catch (err) {
      // Fallback for older browsers or failed clipboard access
      console.warn("Failed to copy to clipboard:", err);
    }
  };

  // Clean up timeout to prevent memory leaks
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  return (
    <div className={`rounded-lg overflow-hidden ${className}`}>
      {title && (
        <div
          className={`px-4 py-2 text-sm font-medium ${docsColors.bg.card} ${docsColors.text.muted} border-b ${docsColors.border.default} rounded-t-lg`}
        >
          {title}
        </div>
      )}
      <div
        className={`relative ${docsColors.bg.code} border ${docsColors.border.default} ${title ? "rounded-b-lg" : "rounded-lg"}`}
      >
        <button
          onClick={handleCopy}
          className={`absolute top-2 right-2 p-2 rounded ${docsColors.bg.card} ${docsColors.text.muted} hover:${docsColors.text.body} transition-colors z-10 border ${docsColors.border.default}`}
          aria-label="Copy code"
        >
          {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
        </button>
        <pre className="p-4 overflow-x-auto">
          <code className={`language-${language} ${docsColors.text.onDark} text-sm font-mono`}>
            {children}
          </code>
        </pre>
      </div>
    </div>
  );
}
